import { Injectable } from '@angular/core';
import { AbstractServiceRecord } from '@common/data-models/service/abstract-service-record';
import { IRealtimeUpdatesProvider } from '@common/interfaces/i-realtime-updates-provider';
import { BroadcastEventListener, ISignalRConnection, SignalR, SignalRConfiguration } from '@henkkelder/ng2-signalr';
import { ServiceCollectionRecord } from '@src/suite/common/data-models/service/service-collection-record';
import { ServiceRecord } from '@src/suite/common/data-models/service/service-record';
import { isCollectionType } from '@src/suite/common/utils/record-utils';
import { Subscription } from 'rxjs';

import { Convertor } from './convertor';
import { NomWebServicesJson } from './nom-web-services-json';

@Injectable({ providedIn: 'root' })
export class RealtimeUpdatesSignalR implements IRealtimeUpdatesProvider {
    private convertTimeStart: number;
    private convertTime: number = 0;
    private rtuTimeStart: number;
    private rtuTime: number = 0;
    private signalRConnection: ISignalRConnection;
    private addUpdateListener: BroadcastEventListener<any>;
    private moveListener: BroadcastEventListener<any>;
    private handlerV2: (rtu: StorytellingRecord, providerName?: string) => void;
    private disconnectHandler: () => void = null;
    private disconnecting: boolean = false;
    private signalRConnectionStatusSub: Subscription;
    private addUpdateListenerSub: Subscription;
    private moveListenerSub: Subscription;

    constructor(
        private signalR: SignalR,
        public convertor: Convertor,
        public nomWebServicesJson: NomWebServicesJson
    ) {}

    public getRTUTime(): number {
        return this.rtuTime;
    }

    public getConvertTime(): number {
        return this.convertTime;
    }

    public async connectV2(endpointUrl: string, handler: (rtu: StorytellingRecord, providerName?: string) => void) {
        this.handlerV2 = handler;
        const c = new SignalRConfiguration();
        c.hubName = 'RealTimeUpdatesHub';
        c.url = `${endpointUrl}/signalR`;
        c.logging = true;
        if (this.signalRConnection) {
            try {
                await this.signalRConnection.start();
            } catch (e) {
                console.log(e);
            }
        } else {
            this.signalRConnection = await this.signalR.connect(c);
        }
        this.signalRConnectionStatusSub = this.signalRConnection.status.subscribe(connectionStatus => {
            if (connectionStatus.value === 2) {
                this.signalRConnection.stop();
            }
            if (connectionStatus.value === 4 && !this.disconnecting) {
                this.forceDisconnect();
                if (this.disconnectHandler) this.disconnectHandler();
            }
        });
        this.addUpdateListener = this.signalRConnection.listenForRaw('AddUpdateRecord');
        this.addUpdateListenerSub = this.addUpdateListener.subscribe((rec: [AbstractServiceRecord, string]) => {
            if (rec[1] !== this.nomWebServicesJson.NomTokenId) {
                this.internalHandlerV2(rec[0]);
            }
        });
        this.moveListener = this.signalRConnection.listenForRaw('MoveRecordPST');
        this.moveListenerSub = this.moveListener.subscribe((rec: [AbstractServiceRecord, string]) => {
            if (rec[1] !== this.nomWebServicesJson.NomTokenId) {
                this.internalHandlerV2(rec[0]);
            }
        });
    }

    public forceDisconnect() {
        try {
            this.disconnecting = true;
            this.signalRConnection.stop();
            this.disconnecting = false;
            this.signalRConnectionStatusSub.unsubscribe();
            this.signalRConnectionStatusSub = null;
            this.addUpdateListenerSub.unsubscribe();
            this.addUpdateListenerSub = null;
            this.moveListenerSub.unsubscribe();
            this.moveListenerSub = null;
        } catch (e) {
            console.log(e);
        }
    }

    public registerDisconnectHandler(disconnectHandler: () => void) {
        this.disconnectHandler = disconnectHandler;
    }

    public getSubscriptionIdV2(rec: StorytellingRecord): string {
        return this.getSubscriptionIdForPath(rec.Database, rec.Path.fullPath);
    }

    public getSubscriptionIdForPath(database: string, path: string): string {
        return `D=${database}&P=${path}`;
    }

    public async subscribe(subscriptionId: string) {
        try {
            while (!this.signalRConnection) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
            await this.signalRConnection.invoke('JoinGroup', subscriptionId);
        } catch (e) {
            console.log(e);
        }
    }

    private internalHandlerV2(rec: AbstractServiceRecord) {
        try {
            if (rec.IsCollectionDiff) {
                this.convertTimeStart = Date.now();
                const serviceCollectionRecord = ServiceCollectionRecord.instance(rec);
                const collectionRecord = this.convertor.fromServiceCollectionRecordV2(serviceCollectionRecord);
                if (!collectionRecord) {
                    // Couldn't build collection record - ignore the RTU
                    return;
                }
                this.convertTime = Date.now() - this.convertTimeStart;
                this.rtuTimeStart = Date.now();
                collectionRecord.isCollectionDiff = true;
                this.handlerV2(collectionRecord, 'enps');
                this.rtuTime = Date.now() - this.rtuTimeStart;
            } else if (isCollectionType(rec.ListData.Type)) {
                const serviceCollectionRecord = ServiceCollectionRecord.instance(rec);
                const collectionRecord = this.convertor.fromServiceCollectionRecordV2<StorytellingCollectionRecord>(serviceCollectionRecord);
                this.rtuTimeStart = Date.now();
                collectionRecord.isCollectionDiff = false;
                this.handlerV2(collectionRecord, 'enps');
                this.rtuTime = Date.now() - this.rtuTimeStart;
            } else {
                const serviceRecord: ServiceRecord = Object.assign(new ServiceRecord(), rec);
                serviceRecord.instantiateChildren();
                const record = this.convertor.fromServiceRecordV2(serviceRecord);
                this.handlerV2(record, 'enps');
            }
        } catch (e) {
            console.log(e);
        }
    }
}
